<script setup>
// NOTICE: KAPTION-INTEGRATION
// Main Sales Funnel Kanban board - horizontal flex container of columns
// Manages data fetching, drag-and-drop between columns, and drawer overlay

import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import {
  FUNNEL_STAGES,
  getConversationStage,
} from 'dashboard/constants/salesFunnel.js';
import { useKaptionSalesKanban } from 'dashboard/composables/useKaptionSalesKanban.js';
import KaptionSalesKanbanColumn from './KaptionSalesKanbanColumn.vue';
import KaptionSalesKanbanDrawer from './KaptionSalesKanbanDrawer.vue';
import { emitter } from 'shared/helpers/mitt';

defineProps({
  isDarkMode: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();

const {
  columnData,
  drawerConversation,
  isDrawerOpen,
  fetchAllColumns,
  loadMore,
  moveConversation,
  openDrawer,
  closeDrawer,
} = useKaptionSalesKanban();

// Track conversation-to-stage mapping for drag events
// When vuedraggable fires 'added' on the target, we need to know the source stage
const conversationStageMap = ref({});

// Build the map whenever columns change
function rebuildStageMap() {
  const map = {};
  FUNNEL_STAGES.forEach(stage => {
    const col = columnData[stage.key];
    if (col?.conversations) {
      col.conversations.forEach(c => {
        map[c.id] = stage.key;
      });
    }
  });
  conversationStageMap.value = map;
}

// Flat ordered list of all conversations across all columns (for prev/next nav)
const allConversations = computed(() => {
  const list = [];
  FUNNEL_STAGES.forEach(stage => {
    const col = columnData[stage.key];
    if (col?.conversations) {
      list.push(...col.conversations);
    }
  });
  return list;
});

const currentDrawerIndex = computed(() => {
  if (!drawerConversation.value?.id) return -1;
  return allConversations.value.findIndex(
    c => c.id === drawerConversation.value.id
  );
});

const hasPrev = computed(() => currentDrawerIndex.value > 0);
const hasNext = computed(
  () =>
    currentDrawerIndex.value >= 0 &&
    currentDrawerIndex.value < allConversations.value.length - 1
);

// Focused column/card for keyboard navigation
const focusedColumnIndex = ref(0);
const focusedCardIndex = ref(-1);

// Fetch all columns on mount
onMounted(() => {
  fetchAllColumns().then(rebuildStageMap);
});

// Listen for conversation update events via websocket
let updateDebounce = null;
function handleConversationUpdate() {
  // Debounce to avoid flooding on bulk updates
  clearTimeout(updateDebounce);
  updateDebounce = setTimeout(() => {
    fetchAllColumns().then(rebuildStageMap);
  }, 2000);
}

onMounted(() => {
  emitter.on('conversation_updated', handleConversationUpdate);
});

onUnmounted(() => {
  emitter.off('conversation_updated', handleConversationUpdate);
  clearTimeout(updateDebounce);
});

/**
 * Handle drag-add from column (a card was dropped INTO a column)
 * The column emits: { conversation, toStage }
 * We look up the source stage from our map
 */
async function handleDragAdd({ conversation, toStage }) {
  if (!conversation?.id) return;

  const fromStage =
    conversationStageMap.value[conversation.id] ||
    getConversationStage(conversation.labels || []);

  if (fromStage === toStage) return;

  // Update the map immediately
  conversationStageMap.value[conversation.id] = toStage;

  try {
    await moveConversation(conversation.id, fromStage, toStage);
  } catch {
    // Revert map
    conversationStageMap.value[conversation.id] = fromStage;
    useAlert('Failed to move conversation. The card has been reverted.');
    // Refresh both columns to revert
    fetchAllColumns().then(rebuildStageMap);
  }
}

/**
 * Handle card click - open the drawer
 */
async function handleCardClick(conversation) {
  if (!conversation?.id) return;

  // Ensure the conversation is loaded in the Vuex store before setting it active
  try {
    await store.dispatch('getConversation', conversation.id);
  } catch {
    // May already be in store
  }

  // Now set it as the active chat (it should exist in the store now)
  const storeConversation =
    store.getters.getConversationById(conversation.id) || conversation;
  store.dispatch('setActiveChat', { data: storeConversation });
  openDrawer(conversation);
}

function handlePrev() {
  if (!hasPrev.value) return;
  handleCardClick(allConversations.value[currentDrawerIndex.value - 1]);
}

function handleNext() {
  if (!hasNext.value) return;
  handleCardClick(allConversations.value[currentDrawerIndex.value + 1]);
}

/**
 * Handle drawer close
 */
function handleDrawerClose() {
  // Don't clearSelectedState here — the drawer DOM persists (v-show) to keep
  // iframes alive, so clearing would crash sidebar components that reference
  // the now-undefined chat. The state is replaced when a new card is opened.
  closeDrawer();
  // Refresh all columns since labels may have changed in the drawer
  fetchAllColumns().then(rebuildStageMap);
}

// Keyboard navigation
function handleKeydown(e) {
  // Escape closes drawer
  if (e.key === 'Escape' && isDrawerOpen.value) {
    handleDrawerClose();
    return;
  }

  // Arrow key navigation when drawer is closed
  if (isDrawerOpen.value) return;

  const stageCount = FUNNEL_STAGES.length;

  if (e.key === 'ArrowRight') {
    e.preventDefault();
    focusedColumnIndex.value = Math.min(
      focusedColumnIndex.value + 1,
      stageCount - 1
    );
    focusedCardIndex.value = 0;
  } else if (e.key === 'ArrowLeft') {
    e.preventDefault();
    focusedColumnIndex.value = Math.max(focusedColumnIndex.value - 1, 0);
    focusedCardIndex.value = 0;
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    const stage = FUNNEL_STAGES[focusedColumnIndex.value];
    const col = columnData[stage.key];
    const maxIdx = (col?.conversations?.length || 1) - 1;
    focusedCardIndex.value = Math.min(focusedCardIndex.value + 1, maxIdx);
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    focusedCardIndex.value = Math.max(focusedCardIndex.value - 1, 0);
  } else if (e.key === 'Enter' && focusedCardIndex.value >= 0) {
    e.preventDefault();
    const stage = FUNNEL_STAGES[focusedColumnIndex.value];
    const col = columnData[stage.key];
    const conv = col?.conversations?.[focusedCardIndex.value];
    if (conv) {
      handleCardClick(conv);
    }
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown, true);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown, true);
});
</script>

<template>
  <div class="kanban-board">
    <div class="kanban-columns" :class="{ 'drawer-active': isDrawerOpen }">
      <KaptionSalesKanbanColumn
        v-for="stage in FUNNEL_STAGES"
        :key="stage.key"
        :stage="stage"
        :conversations="columnData[stage.key]?.conversations || []"
        :loading="columnData[stage.key]?.loading || false"
        :total="columnData[stage.key]?.total || 0"
        :has-more="columnData[stage.key]?.hasMore || false"
        :is-dark-mode="isDarkMode"
        @card-click="handleCardClick"
        @load-more="loadMore"
        @drag-add="handleDragAdd"
      />
    </div>

    <!-- Drawer overlay -->
    <KaptionSalesKanbanDrawer
      :is-open="isDrawerOpen"
      :conversation="drawerConversation"
      :is-dark-mode="isDarkMode"
      :has-prev="hasPrev"
      :has-next="hasNext"
      @close="handleDrawerClose"
      @prev="handlePrev"
      @next="handleNext"
    />
  </div>
</template>

<style scoped lang="scss">
.kanban-board {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.kanban-columns {
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem;
  overflow-x: auto;
  flex: 1;
  min-height: 0;
  align-items: stretch;

  // Custom horizontal scrollbar
  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(128, 128, 128, 0.3);
    border-radius: 3px;
  }
}

// When drawer is open, constrain scroll area so columns overflow and become scrollable
.kanban-columns.drawer-active {
  max-width: 33vw;

  :deep(.kanban-column) {
    flex: 0 0 280px;
  }
}

// Responsive: hide toggle pill on mobile
@media (max-width: 768px) {
  .kanban-columns {
    padding: 0.25rem;
    gap: 0.25rem;
  }
}
</style>
